import React from 'react';
import {
    Checkbox,
    Dropdown,
    IDropdownOption,
    Link,
    MessageBar,
    MessageBarType,
    Stack,
    Text,
    Toggle,
    useTheme,
} from '@fluentui/react';
import { ICrmFolioCandidate, ICrmFolioCapability, ICrmFolioStorefrontSlot, ICrmFolioStorefrontSlotsRequest, CRM_SLOT_DISABLED } from '../../models/Extension';
import { CRM_SETUP_STRINGS } from './wizardStrings';

// The wizard's "what should this connection do" step.
//
// The folio's sub-extensions are presented as three plain-language capabilities (grouped by
// extension type), each a single toggle, with the storefront integration slots folded into the
// groups they serve. The raw per-sub-extension checkboxes and per-slot dropdowns still exist,
// behind an Advanced expander, so nothing the folio panel could do is lost - it is just not the
// first thing a first-time admin has to read.

/** A headline capability: one toggle covering every sub-extension of the listed types. */
interface ICapabilityGroup {
    key: string;
    label: string;
    description: string;
    types: string[];
    /** The storefront slot this capability serves, when it has one. */
    slot?: keyof ICrmFolioStorefrontSlotsRequest;
}

export const CAPABILITY_GROUPS: ICapabilityGroup[] = [
    {
        key: 'import',
        label: CRM_SETUP_STRINGS.capabilityImport,
        description: CRM_SETUP_STRINGS.capabilityImportDescription,
        // Both contact-import spellings appear in live folio data (cwpsa ships 'customerImport').
        types: ['import', 'contactImport', 'customerImport', 'purchaseHistory'],
    },
    {
        key: 'opportunity',
        label: CRM_SETUP_STRINGS.capabilityOpportunity,
        description: CRM_SETUP_STRINGS.capabilityOpportunityDescription,
        types: ['storefrontOpportunity'],
        slot: 'opportunity',
    },
    {
        key: 'customerLookup',
        label: CRM_SETUP_STRINGS.capabilityCustomerLookup,
        description: CRM_SETUP_STRINGS.capabilityCustomerLookupDescription,
        types: ['storefrontCustomer'],
        slot: 'customerLookup',
    },
];

/** The slot-only wiring with no headline capability: locations feed a dropdown, not a toggle. */
const SLOT_TYPES: Record<keyof ICrmFolioStorefrontSlotsRequest, string> = {
    opportunity: 'storefrontOpportunity',
    customerLookup: 'storefrontCustomer',
    locationLookup: 'locations',
};

const SLOT_LABELS: Record<keyof ICrmFolioStorefrontSlotsRequest, string> = {
    opportunity: CRM_SETUP_STRINGS.slotOpportunity,
    customerLookup: CRM_SETUP_STRINGS.slotCustomerLookup,
    locationLookup: CRM_SETUP_STRINGS.slotLocationLookup,
};

export interface ICrmCapabilitiesStepProps {
    candidate: ICrmFolioCandidate;
    /** The selected sub-extension identifiers; the dialog owns this state. */
    selection: string[];
    onSelectionChange: (next: string[]) => void;
    /** Per-slot choices; undefined leaves a slot untouched. The dialog owns this state. */
    slots: ICrmFolioStorefrontSlotsRequest;
    onSlotsChange: (next: ICrmFolioStorefrontSlotsRequest) => void;
}

const capabilitiesOfTypes = (candidate: ICrmFolioCandidate, types: string[]): ICrmFolioCapability[] =>
    (candidate.capabilities ?? []).filter(c => !!c.extensionType && types.includes(c.extensionType));

/** The identifier a slot should default to: the folio's first sub-extension of the slot's type. */
export const defaultSlotIdentifier = (
    candidate: ICrmFolioCandidate, slot: keyof ICrmFolioStorefrontSlotsRequest): string | undefined =>
    capabilitiesOfTypes(candidate, [SLOT_TYPES[slot]])[0]?.identifier;

const CrmCapabilitiesStep: React.FC<ICrmCapabilitiesStepProps> = (props) => {
    const theme = useTheme();
    const [showAdvanced, setShowAdvanced] = React.useState(false);

    const { candidate, selection, onSelectionChange, slots, onSlotsChange } = props;

    const groupState = (group: ICapabilityGroup) => {
        const members = capabilitiesOfTypes(candidate, group.types);
        return { members, on: members.some(m => selection.includes(m.identifier)) };
    };

    const toggleGroup = (group: ICapabilityGroup, on: boolean) => {
        const members = capabilitiesOfTypes(candidate, group.types).map(m => m.identifier);
        const next = on
            ? [...selection, ...members.filter(m => !selection.includes(m))]
            : selection.filter(s => !members.includes(s));
        onSelectionChange(next);
        // The slot follows its capability: turning a capability on wires its storefront slot to
        // this connection unless something else already holds it; turning it off releases only a
        // wiring this wizard visit made.
        if (group.slot) {
            const stored = slotFor(candidate, group.slot);
            if (on && !stored?.assignedElsewhere && !stored?.disabled) {
                onSlotsChange({ ...slots, [group.slot]: defaultSlotIdentifier(candidate, group.slot) });
            } else if (!on && slots[group.slot] !== undefined) {
                onSlotsChange({ ...slots, [group.slot]: undefined });
            }
        }
    };

    const toggleCapability = (identifier: string, on: boolean) => {
        onSelectionChange(on
            ? [...selection, identifier]
            : selection.filter(s => s !== identifier));
    };

    const slotFor = (c: ICrmFolioCandidate, slot: keyof ICrmFolioStorefrontSlotsRequest): ICrmFolioStorefrontSlot | undefined =>
        c.storefrontSlots?.[slot];

    const slotDropdownOptions = (slot: keyof ICrmFolioStorefrontSlotsRequest): IDropdownOption[] => [
        { key: '__leave', text: CRM_SETUP_STRINGS.slotLeave },
        ...capabilitiesOfTypes(candidate, [SLOT_TYPES[slot]]).map(c => ({
            key: c.identifier,
            text: CRM_SETUP_STRINGS.slotUse(c.name ?? c.identifier),
        })),
        { key: CRM_SLOT_DISABLED, text: CRM_SETUP_STRINGS.slotDisabled },
    ];

    const anyAssignedElsewhere = (Object.keys(SLOT_TYPES) as (keyof ICrmFolioStorefrontSlotsRequest)[])
        .some(slot => slotFor(candidate, slot)?.assignedElsewhere && slots[slot] !== undefined);

    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            <Text variant="small">{CRM_SETUP_STRINGS.capabilitiesIntro(candidate.name)}</Text>

            {CAPABILITY_GROUPS.map(group => {
                const { members, on } = groupState(group);
                if (members.length === 0) return null;
                return (
                    <Stack key={group.key} tokens={{ childrenGap: '0.25em' }}>
                        <Toggle
                            label={group.label}
                            inlineLabel
                            checked={on}
                            onChange={(_e, checked) => toggleGroup(group, checked ?? false)}
                        />
                        <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            {group.description}
                        </Text>
                        {group.slot && on && slotFor(candidate, group.slot)?.assignedElsewhere && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                {CRM_SETUP_STRINGS.slotAssignedElsewhere(SLOT_LABELS[group.slot])}
                            </MessageBar>
                        )}
                    </Stack>
                );
            })}

            <Link onClick={() => setShowAdvanced(!showAdvanced)}>
                {showAdvanced ? CRM_SETUP_STRINGS.advancedHide : CRM_SETUP_STRINGS.advancedShow}
            </Link>

            {showAdvanced && (
                <Stack tokens={{ childrenGap: '1em' }}>
                    <Stack tokens={{ childrenGap: '0.5em' }}>
                        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                            {CRM_SETUP_STRINGS.advancedCapabilitiesHeading}
                        </Text>
                        {(candidate.capabilities ?? []).map(capability => (
                            <Checkbox
                                key={capability.identifier}
                                label={capability.name ?? capability.identifier}
                                checked={selection.includes(capability.identifier)}
                                onChange={(_e, checked) => toggleCapability(capability.identifier, checked ?? false)}
                            />
                        ))}
                    </Stack>
                    <Stack tokens={{ childrenGap: '0.5em' }}>
                        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                            {CRM_SETUP_STRINGS.advancedSlotsHeading}
                        </Text>
                        {(Object.keys(SLOT_TYPES) as (keyof ICrmFolioStorefrontSlotsRequest)[]).map(slot => {
                            const options = slotDropdownOptions(slot);
                            // Only the leave/disabled choices exist when the folio has nothing of
                            // this type; a dropdown with no real choice is noise.
                            if (options.length <= 2) return null;
                            return (
                                <Dropdown
                                    key={slot}
                                    label={SLOT_LABELS[slot]}
                                    options={options}
                                    selectedKey={slots[slot] === undefined ? '__leave' : slots[slot]}
                                    onChange={(_e, option) => {
                                        if (!option) return;
                                        onSlotsChange({
                                            ...slots,
                                            [slot]: option.key === '__leave' ? undefined : String(option.key),
                                        });
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Stack>
            )}

            {anyAssignedElsewhere && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    {CRM_SETUP_STRINGS.slotOverwriteWarning}
                </MessageBar>
            )}
        </Stack>
    );
};

export default CrmCapabilitiesStep;
